/**
 * NeedsAttention — the one thing on the dashboard that asks you to DO
 * something.
 *
 * This used to be a single sentence at the bottom of the greeting card
 * ("3 tasks are overdue"), which is the right information in the wrong place
 * twice over: it was the only actionable line on the page and it was not
 * clickable, and it sat inside a decorative band that made the greeting the
 * tallest card on screen.
 *
 * It is a card of its own now, beside "At a glance", and every line is a way
 * IN: overdue work, work due today, the next thing on the calendar, and
 * anything the AI is still chewing through. Each row goes somewhere.
 *
 * WHEN THERE IS NOTHING it says so, once, calmly. A dashboard that invents
 * urgency to avoid an empty state teaches people to ignore it.
 */

import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PiBellRingingLight,
  PiCalendarCheckLight,
  PiCheckCircleLight,
  PiClockCountdownLight,
  PiSpinnerGapLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import GlassCard from '../../shared/brand/GlassCard';
import {
  DashboardCalendarEvent,
  DashboardProcessingItem,
  DashboardUrgentTask,
} from '../../../store/reducer/DashboardReducer';
import { FieldTone } from '../../shared/brand/Field';
import { parseServerDate } from '../../../utils/functions';

export interface NeedsAttentionProps {
  tasks?: DashboardUrgentTask[];
  events?: DashboardCalendarEvent[];
  processing?: DashboardProcessingItem[];
}

const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear()
  && a.getMonth() === b.getMonth()
  && a.getDate() === b.getDate();

interface CalloutRow {
  key: string;
  icon: React.ReactNode;
  label: string;
  tone: FieldTone;
  onClick: () => void;
}

/** Tone → the dot and the glyph. Status colour, never decoration. */
const TONE_TEXT: Record<FieldTone, string> = {
  default: 'text-fg-muted',
  info: 'text-info-600 dark:text-info-400',
  success: 'text-success-600 dark:text-success-400',
  warning: 'text-warning-600 dark:text-warning-400',
  danger: 'text-danger-600 dark:text-danger-400',
};

const NeedsAttention: React.FC<NeedsAttentionProps> = ({
  tasks = [],
  events = [],
  processing = [],
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const rows = useMemo<CalloutRow[]>(() => {
    const now = new Date();
    let overdue = 0;
    let dueToday = 0;
    tasks.forEach((task) => {
      if (!task.due_date) return;
      const due = parseServerDate(task.due_date);
      if (!due) return;
      if (isSameDay(due, now)) dueToday += 1;
      else if (due < now) overdue += 1;
    });

    const out: CalloutRow[] = [];

    if (overdue > 0) {
      out.push({
        key: 'overdue',
        icon: <PiWarningCircleLight />,
        label: String(t('{{count}} tasks are overdue', { count: overdue })),
        tone: 'danger',
        onClick: () => history.push('/tasks'),
      });
    }
    if (dueToday > 0) {
      out.push({
        key: 'today',
        icon: <PiClockCountdownLight />,
        label: String(t('{{count}} tasks are due today', { count: dueToday })),
        tone: 'warning',
        onClick: () => history.push('/tasks'),
      });
    }

    const nextToday = events.find((event) => {
      if (!event.start_time) return false;
      const start = new Date(event.start_time);
      return !Number.isNaN(start.getTime()) && isSameDay(start, now) && start >= now;
    });
    if (nextToday) {
      out.push({
        key: 'next',
        icon: <PiCalendarCheckLight />,
        label: String(t('Next up: {{title}}', { title: nextToday.title })),
        tone: 'info',
        onClick: () => history.push('/calendar'),
      });
    }

    if (processing.length > 0) {
      out.push({
        key: 'processing',
        icon: <PiSpinnerGapLight />,
        label: String(t('{{count}} items are still processing', { count: processing.length })),
        tone: 'info',
        onClick: () => history.push('/matters'),
      });
    }

    return out;
  }, [tasks, events, processing, t, history]);

  return (
    <GlassCard
      icon={<PiBellRingingLight />}
      title={t('Needs you')}
      padding="none"
      className="overflow-hidden"
      fill
      bodyClassName="flex min-h-0 flex-1 flex-col"
      data-testid="needs-attention"
    >
      {rows.length === 0 ? (
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-1.5 px-5 py-4 text-center">
          <PiCheckCircleLight aria-hidden className="h-6 w-6 text-success-500" />
          <p className="text-sm text-fg-body">{t('Nothing overdue — a clear run at it.')}</p>
        </div>
      ) : (
        <div className="custom-scrollbar-main flex min-h-0 flex-1 flex-col divide-y divide-hairline-soft overflow-y-auto [&>*]:flex-shrink-0">
          {rows.map((row) => (
            <button
              key={row.key}
              type="button"
              data-testid={`needs-attention-${row.key}`}
              onClick={row.onClick}
              className="flex items-center gap-3 px-5 py-3 text-left transition-colors duration-200 ease-slide hover:bg-surface-sunken/70 focus:bg-surface-sunken/70 focus:outline-none"
            >
              <span className={clsx('flex-shrink-0 [&>svg]:h-4 [&>svg]:w-4', TONE_TEXT[row.tone])}>
                {row.icon}
              </span>
              <span className="min-w-0 flex-1 truncate text-sm text-gray-700 dark:text-gray-200">
                {row.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </GlassCard>
  );
};

export default NeedsAttention;
